import { z } from 'zod'
import { emailSchema } from '../validators/email.validator.js'

/** Login request body. Shared so the web form and the API validate identically. */
export const loginInputSchema = z
  .object({
    email: emailSchema,
    password: z.string().min(1, 'Password is required'),
  })
  .strict()

export type LoginInput = z.infer<typeof loginInputSchema>

/**
 * Body for `POST /auth/refresh` and `POST /auth/logout`. The opaque refresh
 * token is the credential — refresh exchanges it for a new session; logout
 * revokes it server-side.
 */
export const refreshInputSchema = z
  .object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
  })
  .strict()

export type RefreshInput = z.infer<typeof refreshInputSchema>

/**
 * Body for `POST /auth/enter-tenant`. A SUPER_ADMIN presents their current
 * refresh token (rotated like refresh) and the tenant to enter. Returns a new
 * session scoped to that tenant.
 */
export const enterTenantInputSchema = z
  .object({
    tenantId: z.string().uuid(),
    refreshToken: z.string().min(1, 'Refresh token is required'),
  })
  .strict()

export type EnterTenantInput = z.infer<typeof enterTenantInputSchema>

/**
 * Body for `POST /auth/exit-tenant`. A SUPER_ADMIN presents their current
 * refresh token; the new session has no active tenant (platform-only again).
 */
export const exitTenantInputSchema = z
  .object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
  })
  .strict()

export type ExitTenantInput = z.infer<typeof exitTenantInputSchema>

/**
 * Body for `POST /auth/enter-venue`. An ORGANIZATION_ADMIN (or a SUPER_ADMIN
 * already in tenant support mode) presents their current refresh token and the
 * venue to enter. Returns a new session whose venue authority is that venue
 * alone.
 */
export const enterVenueInputSchema = z
  .object({
    venueId: z.string().uuid(),
    refreshToken: z.string().min(1, 'Refresh token is required'),
  })
  .strict()

export type EnterVenueInput = z.infer<typeof enterVenueInputSchema>

/**
 * Body for `POST /auth/exit-venue`. Drops the venue overlay and returns the
 * session to the scope it had before entering — org-wide for an
 * ORGANIZATION_ADMIN, tenant support mode for a SUPER_ADMIN.
 */
export const exitVenueInputSchema = z
  .object({
    refreshToken: z.string().min(1, 'Refresh token is required'),
  })
  .strict()

export type ExitVenueInput = z.infer<typeof exitVenueInputSchema>

/**
 * Body for `PUT /auth/me/primary-venue`. Sets which venue the caller lands in on
 * sign-in. Must be a venue they can already reach — the API re-checks.
 */
export const setPrimaryVenueInputSchema = z
  .object({
    venueId: z.string().uuid(),
  })
  .strict()

export type SetPrimaryVenueInput = z.infer<typeof setPrimaryVenueInputSchema>

/**
 * Custom claims embedded in the access JWT (plus standard `sub` / time claims).
 * Validated after `jwtVerify` so free-form payload bags never drive authz.
 */
export const accessTokenClaimsSchema = z
  .object({
    sub: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    /** SUPER_ADMIN support mode only; omit when not entered into a tenant. */
    activeTenantId: z.string().uuid().optional(),
    /**
     * Venue support mode only; omit when not entered into a venue. Present for
     * an ORGANIZATION_ADMIN, or a SUPER_ADMIN who entered a venue from inside a
     * tenant (in which case `activeTenantId` is set too).
     */
    activeVenueId: z.string().uuid().optional(),
  })
  .strict()

export type AccessTokenClaims = z.infer<typeof accessTokenClaimsSchema>
